/**
 * @Author: Klaudie <klaudie@foxprofi.cz>
 */

import { STORAGE_KEYS, TOKEN_REFRESH_THRESHOLD } from './constants'

interface TokenPayload {
    exp: number
    iat: number
    sub: string
    [key: string]: unknown
}

/**
 * Dekóduje JWT token a vrátí payload
 */
export function decodeToken(token: string): TokenPayload | null {
    try {
        const base64Url = token.split('.')[1]
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        )
        return JSON.parse(jsonPayload)
    } catch (error) {
        console.error('Failed to decode token:', error)
        return null
    }
}

/**
 * Kontroluje, zda je token expirovaný
 */
export function isTokenExpired(token: string): boolean {
    const payload = decodeToken(token)
    if (!payload) return true

    const now = Math.floor(Date.now() / 1000)
    return payload.exp < now
}

/**
 * Kontroluje, zda by měl být token obnoven
 * Vrací true pokud zbývá méně než TOKEN_REFRESH_THRESHOLD sekund do expirace
 */
export function shouldRefreshToken(token: string): boolean {
    const payload = decodeToken(token)
    if (!payload) return true

    const now = Math.floor(Date.now() / 1000)
    const timeUntilExpiry = payload.exp - now

    return timeUntilExpiry < TOKEN_REFRESH_THRESHOLD
}

/**
 * Uloží tokeny do localStorage
 */
export function setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken)
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken)
}

/**
 * Získá access token z localStorage
 */
export function getAccessToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
}

/**
 * Získá refresh token z localStorage
 */
export function getRefreshToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)
}

/**
 * Smaže všechny tokeny z localStorage
 */
export function clearTokens(): void {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
    localStorage.removeItem(STORAGE_KEYS.USER_PROFILE)
}

/**
 * Kontroluje, zda je uživatel přihlášený (má platný access token)
 */
export function isAuthenticated(): boolean {
    const token = getAccessToken()
    if (!token) return false
    return !isTokenExpired(token)
}
